import React, {FC, useState, useRef} from "react";

import {Table, Space, Modal, Dropdown, Menu, Button, message} from 'antd';
import { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import type { ProDescriptionsActionType } from "@ant-design/pro-descriptions";

import {DownOutlined} from '@ant-design/icons';

import type {Pagination} from './../../../../utils/data/Type'
import { Role, RoleDetailProps } from './../../type/RoleType';
import RoleApi from "../../api/RoleApi";
import RoleAddForm from "./RoleAddForm";
import RoleDetail from "./RoleDetail";

const RolePage:React.FC = ()=>{

    const tableActionRef = useRef<ActionType>();
    const detailActionRef = useRef<ProDescriptionsActionType>();

    const [addFormVisible, setAddFormVisible] = useState<boolean>(false);
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [currentRole, setCurrentRole] = useState<Role>({});

    const refreshData = ()=>{
        tableActionRef.current?.reload();
    }

    const deleteRole = (role:Role)=>{
        Modal.confirm({
            title: "删除系统角色确认",
            content: "确定删除该系统角色吗？",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await RoleApi.deleteRole(role);
                tableActionRef.current?.reload();
            }
        });
    }

    const showAddForm = ()=>{
        setAddFormVisible(true);
    }

    const hideAddForm = ()=>{
        setAddFormVisible(false);
    }

    const showDetail = (role:Role)=>{
        setCurrentRole(role);
        setDetailVisible(true);
    }

    const hideDetail = ()=>{
        setCurrentRole({});
        setDetailVisible(false);
    }

    const rowExtraButtons : React.FC<{
        item: Role
    }> = ({item}) => {
        return (
            <Dropdown
                key="row-extra-btns"
                overlay={
                    <Menu
                        onClick={({key})=>{
                            if(key==='delete') {
                                deleteRole(item);
                            }
                        }}
                    >
                        <Menu.Item key="delete">
                            删除
                        </Menu.Item>
                    </Menu>
                }
            >
                <a>
                    更多 <DownOutlined />
                </a>
            </Dropdown>
        );
    };

    const columns:ProColumns<Role>[] = [
        {
            key: "rowNo",
            title: "序号",
            dataIndex: "rowNo",
            width: 60,
            hideInSearch: true,
            hideInSetting: true,
            hideInForm: true,
            copyable: false,
        },
        {
            key: "code",
            title: "角色编码",
            dataIndex: "code",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "name",
            title: "角色名称",
            dataIndex: "name",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "nameAbbr",
            title: "角色简称",
            dataIndex: "nameAbbr",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "nameAlias",
            title: "角色别名",
            dataIndex: "nameAlias",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "sortNo",
            title: "显示顺序",
            dataIndex: "sortNo",
            width: 0,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            title: '操作',
            valueType: 'option',
            dataIndex: 'actions',
            render: (text, record:Role, _, action) =>{
                return [
                    <a key='view' onClick={()=>{
                        setCurrentRole(record);
                        setDetailVisible(true);
                        }}>
                        查看
                    </a>,
                    rowExtraButtons({item:record}),
                ];
            }
        },
    ];

    return (
        <div>
            <ProTable<Role, Pagination>
                headerTitle="系统角色列表"
                rowKey="id"
                columns={columns}
                actionRef={tableActionRef}
                rowSelection={{
                    alwaysShowAlert:true,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
                    defaultSelectedRowKeys: [],
                }}
                tableAlertRender={({ selectedRowKeys, selectedRows, onCleanSelected }) => (
                    <Space size={24}>
                          <span>
                            已选 {selectedRowKeys.length} 项
                            <a style={{ marginLeft: 8 }} onClick={onCleanSelected}>
                              取消选择
                            </a>
                          </span>
                    </Space>
                )}
                tableAlertOptionRender={({selectedRowKeys}) => {
                    return (
                        <Space size={16}>
                            <a onClick={async ()=>{
                                Modal.confirm({
                                    title: '删除系统角色',
                                    content: '确认删除所选系统角色吗？',
                                    okText: '删除所选',
                                    cancelText: '取消',
                                    onOk:async ()=>{
                                        const hide = message.loading("数据删除中....");
                                        for(let i=0; i<selectedRowKeys.length; i++){
                                            let id:string = selectedRowKeys[i]+"";
                                            try{
                                                await RoleApi.deleteRole({id:id});
                                            }catch(ex){

                                            }
                                        }
                                        hide();
                                        tableActionRef.current?.reload();
                                        message.success("数据删除完成！");
                                    }
                                });
                            }}>
                                批量删除
                            </a>
                        </Space>
                    );
                }}
                toolBarRender={()=>{
                    return [
                        <Button
                            type="primary"
                            onClick={showAddForm}
                        >
                            新建
                        </Button>
                    ];
                }}
                request={async (params={}, sort, filter)=>{

                    params.page = params.current;
                    const pageInfo = await RoleApi.findRolePage({
                        deletedFlag: false,
                        ...params,
                    });

                    pageInfo.list.forEach((item:any, idx:number)=>{
                        item.rowNo = ((params.current||1)-1)*(params.pageSize||20)+idx+1;
                    });

                    return {
                        success: true,
                        data: pageInfo.list,
                    };
                }}
            />

            <RoleAddForm
                visible={addFormVisible}
                onClose={hideAddForm}
                onRefresh={refreshData}
            />

            <RoleDetail
                visible={detailVisible}
                role={currentRole}
                actionRef={detailActionRef}
                onClose={hideDetail}
                onRefresh={refreshData}
            />
        </div>
    );
};

export default RolePage;